import React from 'react';
import { FormItem } from '@xbcb/form-item-components';
import { NamePath } from '@xbcb/ui-types';
import { useTheme } from 'styled-components';
import { CpscItemType } from '__generated__/graphql';
import ProductDetails from '../ProductDetails';
import LotDetails from '../LotDetails';
import FormListSection from '../FormListSection';
import CpscEntityDetails from '../CpscEntityDetails/component';
import {
  StyledFlexDiv,
  StyledInput,
  StyledSelect,
  StyledDatePicker,
  StyledFieldRow,
} from './styles';

const ITEM_TYPE_OPTIONS = [
  { value: CpscItemType.PRODUCT, label: 'Product' },
  { value: CpscItemType.COMPONENT, label: 'Component' },
];

export interface CpscFullPgaMessageSetProps {
  localNamePath: NamePath;
  fullNamePath: NamePath;
  readOnly?: boolean;
  disabled?: boolean;
}

const CpscFullPgaMessageSet: React.FC<CpscFullPgaMessageSetProps> = ({
  localNamePath,
  fullNamePath,
  readOnly = false,
  disabled = false,
}) => {
  const theme = useTheme();

  return (
    <>
      {/* Row 1: Item Type + Description */}
      <StyledFlexDiv>
        <FormItem
          name={[...localNamePath, 'itemType']}
          label="Item Type"
          $itemSize={theme.size.short}
          $readOnly={readOnly}
          rules={[{ required: true }]}
          validateTrigger="onBlur"
        >
          <StyledSelect
            options={ITEM_TYPE_OPTIONS}
            disabled={disabled || readOnly}
            placeholder="Select item type"
          />
        </FormItem>
        <FormItem
          name={[...localNamePath, 'description']}
          label="Description"
          $itemSize={theme.size.medium}
          $readOnly={readOnly}
        >
          <StyledInput
            maxLength={70}
            disabled={disabled || readOnly}
            placeholder="Enter description for this item"
          />
        </FormItem>
      </StyledFlexDiv>

      {/* Row 2: Test Date */}
      <FormItem
        name={[...localNamePath, 'labTestingInfo', 'testDate']}
        label="Date of Latest Inspection/Testing"
        $itemSize={theme.size.short}
        $readOnly={readOnly}
        rules={[{ required: true }]}
        validateTrigger="onBlur"
      >
        <StyledDatePicker format="MM-DD-YYYY" disabled={disabled || readOnly} />
      </FormItem>

      {/* Product Details Section - includes identifiers, names, characteristics */}
      <ProductDetails
        fullNamePath={fullNamePath}
        localNamePath={localNamePath}
        readOnly={readOnly}
        disabled={disabled}
      />

      {/* Lot Details Section - lot information with add/remove */}
      <LotDetails
        fullNamePath={fullNamePath}
        localNamePath={localNamePath}
        readOnly={readOnly}
        disabled={disabled}
      />

      {/* Entity Details Section - entities with add/remove */}
      <FormListSection
        name={[...(localNamePath as (string | number)[]), 'entities']}
        addButtonLabel="Add Another Entity"
        minItems={1}
        readOnly={readOnly}
        disabled={disabled}
        renderRow={(field, index, helpers) => (
          <StyledFieldRow>
            <CpscEntityDetails
              key={field.key}
              localNamePath={[field.name]}
              fullNamePath={[
                ...(fullNamePath as (string | number)[]),
                'entities',
                index,
              ]}
              index={index}
              showRemove={helpers.showRemove}
              onRemove={helpers.remove}
              readOnly={readOnly}
              disabled={disabled}
            />
          </StyledFieldRow>
        )}
      />
    </>
  );
};

export default CpscFullPgaMessageSet;
